/**
 * Prompt Generator Widget
 * Dynamically builds interactive prompt generation forms based on JSON config
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPromptGenerator);
  } else {
    initPromptGenerator();
  }

  function initPromptGenerator() {
    const configElement = document.getElementById('prompt-tool-config');
    if (!configElement) return;

    try {
      const config = JSON.parse(configElement.textContent);
      setupPromptModal();
      buildPromptGenerator(config);
    } catch (error) {
      console.error('Failed to parse prompt generator config:', error);
    }
  }

  function buildPromptGenerator(config) {
    const fieldsContainer = document.getElementById('prompt-generator-fields');
    const form = document.getElementById('prompt-generator-form');
    const generateBtn = document.getElementById('generate-prompt-btn');
    const resetBtn = document.getElementById('reset-prompt-btn');
    const copyBtn = document.getElementById('copy-prompt-btn');

    if (!fieldsContainer || !form) return;

    // Update header with config title/description if provided
    if (config.title) {
      const titleElement = document.querySelector('.prompt-generator-title');
      if (titleElement) titleElement.textContent = config.title;
    }
    if (config.description) {
      const descElement = document.querySelector('.prompt-generator-description');
      if (descElement) descElement.textContent = config.description;
    }

    // Build form fields from config
    config.modes.forEach((mode, index) => {
      const fieldGroup = createFieldGroup(mode, index);
      fieldsContainer.appendChild(fieldGroup);
    });

    // Handle generate button
    if (generateBtn) {
      generateBtn.addEventListener('click', function(e) {
        e.preventDefault();
        generatePrompt(config, form);
      });
    }

    // Handle reset button
    if (resetBtn) {
      resetBtn.addEventListener('click', function(e) {
        e.preventDefault();
        resetForm(form);
      });
    }

    // Handle copy button
    if (copyBtn) {
      copyBtn.addEventListener('click', function(e) {
        e.preventDefault();
        copyToClipboard();
      });
    }

    // Handle back to editor button
    const backBtn = document.getElementById('back-to-editor-btn');
    if (backBtn) {
      backBtn.addEventListener('click', function(e) {
        e.preventDefault();
        showEditor();
      });
    }
  }

  function createFieldGroup(mode, index) {
    const group = document.createElement('div');
    group.className = 'prompt-field-group';

    // Collapse all but first by default
    if (typeof index === 'number' && index > 0) {
      group.classList.add('collapsed');
    }

    // Create header
    const header = document.createElement('div');
    header.className = 'prompt-field-header';

    const headerLeft = document.createElement('div');
    headerLeft.className = 'prompt-field-header-left';

    const label = document.createElement('label');
    label.className = 'prompt-field-label';
    label.textContent = mode.label;

    const description = document.createElement('p');
    description.className = 'prompt-field-description';
    description.textContent = mode.description;

    headerLeft.appendChild(label);
    headerLeft.appendChild(description);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'collapse-toggle';
    toggle.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');
    toggle.innerHTML = '<span class="chevron"></span>';

    // Make entire header clickable
    header.addEventListener('click', function(e) {
      const isCollapsed = group.classList.contains('collapsed');
      
      if (isCollapsed) {
        // Close all other groups (accordion behavior)
        const allGroups = document.querySelectorAll('.prompt-field-group');
        allGroups.forEach(g => {
          if (g !== group) {
            g.classList.add('collapsed');
            const otherToggle = g.querySelector('.collapse-toggle');
            if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
          }
        });
        
        // Open this group
        group.classList.remove('collapsed');
        toggle.setAttribute('aria-expanded', 'true');
      } else {
        // Close this group
        group.classList.add('collapsed');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });

    header.appendChild(headerLeft);
    header.appendChild(toggle);
    group.appendChild(header);

    // Create options container
    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'prompt-field-options';
    
    // Check if this mode uses radio buttons (single selection)
    const isRadio = mode.type === 'radio';
    if (isRadio) {
      optionsContainer.classList.add('radio-options');
    }

    mode.options.forEach((option, idx) => {
      const optionWrapper = document.createElement('label');
      optionWrapper.className = 'prompt-option';
      if (isRadio) optionWrapper.classList.add('radio-option');

      const input = document.createElement('input');
      input.type = isRadio ? 'radio' : 'checkbox';
      input.name = mode.id;
      input.value = option.value;
      input.id = `${mode.id}-${idx}`;
      input.className = isRadio ? 'prompt-radio' : 'prompt-checkbox';

      const optionLabel = document.createElement('span');
      optionLabel.className = 'prompt-option-label';
      
      // If option has icon HTML, parse it
      if (option.icon) {
        const iconSpan = document.createElement('span');
        iconSpan.className = 'option-icon';
        iconSpan.innerHTML = option.icon;
        optionLabel.appendChild(iconSpan);
      }
      
      const textSpan = document.createElement('span');
      textSpan.textContent = option.label;
      optionLabel.appendChild(textSpan);

      optionWrapper.appendChild(input);
      optionWrapper.appendChild(optionLabel);
      optionsContainer.appendChild(optionWrapper);
    });

    group.appendChild(optionsContainer);
    return group;
  }

  function generatePrompt(config, form) {
    const formData = new FormData(form);
    const selections = {};
    const selectedOptions = [];

    // Collect all selections
    config.modes.forEach(mode => {
      const modeSelections = formData.getAll(mode.id);
      if (modeSelections.length > 0) {
        // For radio buttons, store single value instead of array
        selections[mode.id] = mode.type === 'radio' ? modeSelections[0] : modeSelections;
        
        // Get human-readable labels
        modeSelections.forEach(value => {
          const option = mode.options.find(opt => opt.value === value);
          if (option) {
            selectedOptions.push({
              mode: mode.label,
              option: option.label,
              value: value
            });
          }
        });
      }
    });

    // Build natural language prompt
    const promptParts = [];
    config.modes.forEach(mode => {
      const selection = selections[mode.id];
      if (selection) {
        // Handle both single values (radio) and arrays (checkbox)
        const values = Array.isArray(selection) ? selection : [selection];
        if (values.length > 0) {
          const selectedLabels = values.map(value => {
            const option = mode.options.find(opt => opt.value === value);
            return option ? option.label : value;
          });
          promptParts.push(`${mode.label}: ${selectedLabels.join(', ')}`);
        }
      }
    });

    const naturalPrompt = promptParts.length > 0 
      ? `Photorealistic sunset sky image with the following parameters:\n\n${promptParts.join('\n')}\n\nStyle: Hyper-realistic, high detail atmospheric photography, natural lighting physics, authentic color gradients, professional sky photography quality`
      : 'No selections made';

    // Build structured JSON output
    const output = {
      prompt: naturalPrompt,
      parameters: selections,
      selections: selectedOptions,
      metadata: {
        framework: config.title || 'Prompt Generator',
        timestamp: new Date().toISOString(),
        outputFormat: config.outputFormat || 'structured-json'
      }
    };

    displayOutput(output);
  }

  function displayOutput(output) {
    const outputContainer = document.getElementById('prompt-output');
    const outputContent = document.getElementById('prompt-output-content');
    const form = document.getElementById('prompt-generator-form');

    if (!outputContainer || !outputContent) return;

    outputContent.textContent = JSON.stringify(output, null, 2);
    
    // Hide the form and show only output
    if (form) form.style.display = 'none';
    outputContainer.style.display = 'block';
    
    // Smooth scroll to output
    outputContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function resetForm(form) {
    form.reset();
    const outputContainer = document.getElementById('prompt-output');
    if (outputContainer) {
      outputContainer.style.display = 'none';
    }
    
    // Show the form again
    if (form) form.style.display = 'flex';
    
    // Hide success message
    const successMessage = document.getElementById('copy-success-message');
    if (successMessage) {
      successMessage.style.display = 'none';
    }
    
    // Reset all groups to first one open
    const allGroups = document.querySelectorAll('.prompt-field-group');
    allGroups.forEach((g, idx) => {
      if (idx === 0) {
        g.classList.remove('collapsed');
        const toggle = g.querySelector('.collapse-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      } else {
        g.classList.add('collapsed');
        const toggle = g.querySelector('.collapse-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function copyToClipboard() {
    const outputContent = document.getElementById('prompt-output-content');
    const successMessage = document.getElementById('copy-success-message');
    
    if (!outputContent) return;

    const text = outputContent.textContent;
    
    // Use modern clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function() {
        showCopySuccess(successMessage);
      }).catch(function(err) {
        console.error('Failed to copy:', err);
        fallbackCopy(text, successMessage);
      });
    } else {
      fallbackCopy(text, successMessage);
    }
  }

  function fallbackCopy(text, successMessage) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      showCopySuccess(successMessage);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    
    document.body.removeChild(textarea);
  }

  function showCopySuccess(successMessage) {
    if (!successMessage) return;
    
    successMessage.style.display = 'block';
    setTimeout(function() {
      successMessage.style.display = 'none';
    }, 3000);
  }

  function showEditor() {
    const form = document.getElementById('prompt-generator-form');
    const outputContainer = document.getElementById('prompt-output');
    
    if (outputContainer) outputContainer.style.display = 'none';
    if (form) form.style.display = 'flex';
  }

  // Modal helpers
  function setupPromptModal() {
    const modal = document.getElementById('prompt-generator-modal');
    const openBtn = document.getElementById('open-prompt-generator');
    const closeBtn = document.getElementById('close-prompt-generator');
    const overlay = modal ? modal.querySelector('.prompt-modal-overlay') : null;

    if (openBtn && modal) {
      openBtn.addEventListener('click', function() { openPromptModal(modal); });
    }
    if (closeBtn && modal) {
      closeBtn.addEventListener('click', function() { closePromptModal(modal); });
    }
    if (overlay && modal) {
      overlay.addEventListener('click', function() { closePromptModal(modal); });
    }

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal && modal.style.display === 'block') {
        closePromptModal(modal);
      }
    });
  }

  function openPromptModal(modal) {
    modal.style.display = 'block';
    document.body.classList.add('prompt-modal-open');
    // animation hook
    requestAnimationFrame(function() { modal.classList.add('is-visible'); });
  }

  function closePromptModal(modal) {
    modal.classList.remove('is-visible');
    setTimeout(function() {
      modal.style.display = 'none';
      document.body.classList.remove('prompt-modal-open');
    }, 150);
  }

})();
